/**
 * Which station prints which lines.
 *
 * The routing is always the same chain — item → menu item → category →
 * printer — but it is worked out in two different places depending on the
 * moment:
 *
 *   • A NEW order: the server already did it. `POST /orders` returns
 *     `data.printPlan`, built from the menu documents it loaded to validate
 *     the order, so it costs no extra query and cannot disagree with what was
 *     saved. Use it as given.
 *
 *   • An EDITED order, or a reprint: `PATCH /orders/:id` returns no plan, so
 *     the till builds one from the menu and printers it already has in the
 *     store. The same chain, the same result — just assembled here.
 *
 * A category with no printer, or one whose printer is switched off, prints
 * nothing. That is the normal case for anything handed straight to the
 * customer — bottled water, cigarettes, packaged snacks — and those items are
 * skipped rather than pushed onto some other station's paper, where they
 * would be noise on the one document the kitchen has to trust.
 */

const idOf = (value) => {
  if (!value) return '';
  return typeof value === 'object' ? value._id || value.id || '' : String(value);
};

/**
 * The server's plan, checked over.
 *
 * Two things are worth guarding: an old server that omits the key entirely,
 * and an entry with no host, which would print nowhere and is better dropped
 * loudly-in-the-logs than quietly-on-the-floor.
 */
export function normalisePlan(plan) {
  if (!Array.isArray(plan)) return [];

  return plan
    .filter((entry) => entry && entry.printer && entry.printer.host)
    .map((entry) => ({
      printer: {
        id: entry.printer.id || entry.printer._id || '',
        name: entry.printer.name || 'Station',
        host: entry.printer.host,
        port: Number(entry.printer.port) || 9100,
      },
      items: (entry.items || []).map((item) => ({
        name: item.name,
        quantity: Number(item.quantity) || 0,
      })),
    }))
    .filter((entry) => entry.items.length);
}

/**
 * The same plan, built here.
 *
 * @param {Array} items       order items — each needs `menuItem`, `name`, `quantity`
 * @param {Array} products    the menu, for item → category
 * @param {Array} categories  for category → printer
 * @param {Array} printers    for printer → address
 */
export function buildPlan(items, products, categories, printers) {
  const categoryOfProduct = new Map();
  (products || []).forEach((product) => {
    if (product && product._id) categoryOfProduct.set(product._id, idOf(product.category));
  });

  const printerOfCategory = new Map();
  (categories || []).forEach((category) => {
    if (category && category._id) printerOfCategory.set(category._id, idOf(category.printer));
  });

  const printerById = new Map();
  (printers || []).forEach((printer) => {
    // Switched off means no ticket, the same as no printer at all.
    if (printer && printer._id && printer.isActive !== false) {
      printerById.set(printer._id, printer);
    }
  });

  const buckets = new Map();

  (items || []).forEach((item) => {
    const menuItemId = idOf(item.menuItem || item.productId);
    if (!menuItemId) return;

    const categoryId = categoryOfProduct.get(menuItemId);
    if (!categoryId) return;

    const printerId = printerOfCategory.get(categoryId);
    if (!printerId) return;

    const printer = printerById.get(printerId);
    if (!printer || !printer.host) return;

    if (!buckets.has(printerId)) {
      buckets.set(printerId, {
        printer: {
          id: printer._id,
          name: printer.name,
          host: printer.host,
          port: Number(printer.port) || 9100,
        },
        items: [],
      });
    }

    buckets.get(printerId).items.push({
      name: item.name,
      // The cart calls it qty; a saved order calls it quantity.
      quantity: Number(item.quantity ?? item.qty) || 0,
    });
  });

  return Array.from(buckets.values()).filter((entry) => entry.items.length);
}
